import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface ChangeStats {
  readonly total: number;
  readonly approved: number;
  readonly closed: number;
  readonly emergency: number;
  readonly emergencyReviewOverdue: number;
}
interface ReleaseStats {
  readonly total: number;
  readonly approved: number;
  readonly deployed: number;
  readonly withoutRollbackPlan: number;
}
interface BuildStats {
  readonly total: number;
  readonly success: number;
  readonly successRatePct: number;
  readonly belowThreshold: boolean;
}
interface DeploymentStats {
  readonly total: number;
  readonly deployed: number;
  readonly failed: number;
  readonly productionWithoutRelease: number;
  readonly sodViolations: number;
}
interface RollbackStats {
  readonly total: number;
  readonly completed: number;
  readonly failed: number;
}
interface ConfigurationStats {
  readonly total: number;
  readonly active: number;
}
interface GovernanceStatus {
  readonly verdict: 'COMPLIANT' | 'NON-COMPLIANT';
  readonly buildSuccessThresholdPct: number;
  readonly change: ChangeStats;
  readonly release: ReleaseStats;
  readonly build: BuildStats;
  readonly deployment: DeploymentStats;
  readonly rollback: RollbackStats;
  readonly configuration: ConfigurationStats;
  readonly gaps: readonly string[];
}

/**
 * M32 — DevOps Dashboard (§31 / §24). Executive single-glance delivery-governance
 * posture: the overall COMPLIANT / NON-COMPLIANT verdict (§24 COMPLETION RULE),
 * headline counters for change, release, build, deployment, rollback and
 * configuration domains, and the enumerated §33 business-rule gaps (BR-01..BR-07)
 * that must be zero for sign-off. The build-success threshold is resolved from
 * the Global Parameter Master (LAW 6 — never hard-coded).
 */
export function DevopsDashboardPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [data, setData] = useState<GovernanceStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    apiRequest<{ data: GovernanceStatus }>('/v1/devops/governance/status', { token })
      .then((r) => { if (alive) { setData(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  return (
    <main aria-label="devops-dashboard">
      <h1>{t('m32.dashboard.title')}</h1>
      {error && <p role="alert">{error}</p>}
      {loading || !data ? (
        <p>{t('m32.dashboard.loading')}</p>
      ) : (
        <>
          <section aria-label="verdict" data-verdict={data.verdict}>
            <p aria-label="verdict-value">{data.verdict}</p>
            <p aria-label="build-threshold">{t('m32.dashboard.buildThreshold', { pct: data.buildSuccessThresholdPct })}</p>
          </section>
          <section aria-label="stat-cards" className="card-grid">
            <article aria-label="card-change">
              <h2>{t('m32.dashboard.changes')}</h2>
              <p aria-label="change-total">{t('m32.dashboard.total', { count: data.change.total })}</p>
              <p aria-label="change-approved">{t('m32.dashboard.approved', { count: data.change.approved })}</p>
              <p aria-label="change-emergency-overdue">{t('m32.dashboard.reviewOverdue', { count: data.change.emergencyReviewOverdue })}</p>
            </article>
            <article aria-label="card-release">
              <h2>{t('m32.dashboard.releases')}</h2>
              <p aria-label="release-total">{t('m32.dashboard.total', { count: data.release.total })}</p>
              <p aria-label="release-approved">{t('m32.dashboard.approved', { count: data.release.approved })}</p>
              <p aria-label="release-without-rollback">{t('m32.dashboard.withoutRollback', { count: data.release.withoutRollbackPlan })}</p>
            </article>
            <article aria-label="card-build">
              <h2>{t('m32.dashboard.builds')}</h2>
              <p aria-label="build-total">{t('m32.dashboard.total', { count: data.build.total })}</p>
              <p aria-label="build-success-rate">{t('m32.dashboard.successRate', { pct: data.build.successRatePct })}</p>
              <p aria-label="build-below-threshold">{data.build.belowThreshold ? 'BELOW THRESHOLD' : 'OK'}</p>
            </article>
            <article aria-label="card-deployment">
              <h2>{t('m32.dashboard.deployments')}</h2>
              <p aria-label="deployment-total">{t('m32.dashboard.total', { count: data.deployment.total })}</p>
              <p aria-label="deployment-deployed">{t('m32.dashboard.deployed', { count: data.deployment.deployed })}</p>
              <p aria-label="deployment-prod-without-release">{t('m32.dashboard.prodWithoutRelease', { count: data.deployment.productionWithoutRelease })}</p>
              <p aria-label="deployment-sod-violations">{t('m32.dashboard.sodViolations', { count: data.deployment.sodViolations })}</p>
            </article>
            <article aria-label="card-rollback">
              <h2>{t('m32.dashboard.rollbacks')}</h2>
              <p aria-label="rollback-total">{t('m32.dashboard.total', { count: data.rollback.total })}</p>
              <p aria-label="rollback-completed">{t('m32.dashboard.completed', { count: data.rollback.completed })}</p>
              <p aria-label="rollback-failed">{t('m32.dashboard.failed', { count: data.rollback.failed })}</p>
            </article>
            <article aria-label="card-configuration">
              <h2>{t('m32.dashboard.configuration')}</h2>
              <p aria-label="configuration-total">{t('m32.dashboard.versions', { count: data.configuration.total })}</p>
              <p aria-label="configuration-active">{t('m32.dashboard.active', { count: data.configuration.active })}</p>
            </article>
          </section>
          <section aria-label="gaps">
            <h2>{t('m32.dashboard.gapsTitle')}</h2>
            <p aria-label="gap-count">{t('m32.dashboard.gapCount', { count: data.gaps.length })}</p>
            {data.gaps.length === 0 ? (
              <p aria-label="no-gaps">{t('m32.dashboard.noGaps')}</p>
            ) : (
              <ul aria-label="gap-list">
                {data.gaps.map((g) => <li key={g}>{g}</li>)}
              </ul>
            )}
          </section>
        </>
      )}
    </main>
  );
}
